import { useMutation } from '@tanstack/react-query';
import { checkFederation } from '@/features/auth/services/auth';
import type {
  FederationCheckRequest,
  FederationCheckResponse,
} from '@/features/auth/types/auth.types';

interface UseFederationCheckOptions {
  onSuccess?: (
    data: FederationCheckResponse,
    variables: FederationCheckRequest
  ) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for the federated (SSO) domain check
 *
 * Resolves the corporate e-mail domain against the auth-bridge
 * `POST /auth/federation/check` endpoint. When the domain is federated the
 * result carries the IAM-SAML `registrationId` / `authenticationUrl` used to
 * redirect the browser to the organization's IdP.
 *
 * @param options - Callbacks for success and error handling
 * @example
 * ```tsx
 * const { mutate: check, isPending } = useFederationCheck({
 *   onSuccess: (data) => {
 *     if (data.federated && data.authenticationUrl) {
 *       window.location.assign(data.authenticationUrl);
 *     }
 *   },
 * });
 *
 * check({ email: 'user@bancobv.com.br' });
 * ```
 *
 * @since 1.0.0
 * @category Hooks
 */
export function useFederationCheck(options?: UseFederationCheckOptions) {
  return useMutation<FederationCheckResponse, Error, FederationCheckRequest>({
    mutationFn: (payload) => checkFederation(payload),
    onSuccess: (data, variables) => {
      options?.onSuccess?.(data, variables);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}
